import { useState, useRef } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Menu, UserCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { ChangePasswordModal } from "../users/ChangePasswordModal";
import LanguageSelector from "../../components/LanguageSelector";
import coLogo from "../../images/logo.png";
import { useAuth } from "../AuthContext";
import { encryptPassword } from "../../RsaEncryptor";
import { FaKey, FaLock } from "react-icons/fa";
import Util from "../../Util";
import PERMISSIONS from "../../Permissions";

export default function Navbar() {
    const { t, i18n } = useTranslation();
    const language = i18n.language;
    const isRTL = language === "ar" || language === "he";
    const location = useLocation();
    const navigate = useNavigate();

    const [isOpen, setIsOpen] = useState(false);
    const [showLogin, setShowLogin] = useState(false);
    const { user, login, logout, loading } = useAuth();

    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const dropdownRef = useRef();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [moreDropdownOpen, setMoreDropdownOpen] = useState(false);
    const [isPwdModalOpen, setIsPwdModalOpen] = useState(false);

    const canAccessTraffic = (user?.Role === 1 || user?.Role === 2);

    // ======= الروابط الأساسية =======
    const navLinks = [
        { href: "/", label: t("home") },
        { href: "/about", label: t("aboutUs") },
        { href: "/services", label: t("services") },
        { href: "/Fleet", label: t("fleet") },
        { href: "/Media", label: t("media") },
    ];

    // ======= روابط شحناتي =======
    const shipmentLinks = [];
    // if (Util.hasPermission(user, PERMISSIONS.ADD_Order)) shipmentLinks.push({ href: "/SupplierDashboard", label: t("myOrders") });
    // if (Util.hasPermission(user, PERMISSIONS.Receiving_Orders)) shipmentLinks.push({ href: "/EmployeeDashboard", label: t("receivingShipments") });
    // if (user?.Role === 1) shipmentLinks.push({ href: "/ManagerDashboard", label: t("monitorShipments") });
    // if (Util.hasPermission(user, PERMISSIONS.Orders_Report)) shipmentLinks.push({ href: "/ShipmentsReport", label: t("shipmentsReport") });

    if (Util.hasPermission(user, PERMISSIONS.GoTo_SpareParts_Homepage))
        shipmentLinks.push({ href: "/SpareParts", label: t("myOrders") });

    const moreLinks = [
        { href: "/jobs", label: t("applyJob") },
        { href: "/contact", label: t("contactUs") },
        { href: "", label: '', separator: true },
        ...shipmentLinks,
    ];

    // ======= LOGIN HANDLERS =======
    const handleLogin = async () => {
        setError("");
        if (!username || !password) {
            setError(t("please_enter_username_and_password"));
            return;
        }
        const encrypted = encryptPassword(password);
        const result = await login(username, encrypted, i18n);
        if (!result.success) {
            setError(result.message || t("login_failed"));
        } else {
            setShowLogin(false);
            setUsername("");
            setPassword("");
            if (Util.hasPermission(result.user, PERMISSIONS.GoTo_SpareParts_Homepage)) {
                navigate("/SpareParts");
            }
        }
    };

    const handleLogout = () => {
        localStorage.removeItem("traffic_user");
        localStorage.removeItem("traffic_token");
        logout();
        setDropdownOpen(false);
    };

    return (
        <>
            {/* ================= NAVBAR ================= */}
            <header className="sticky top-0 z-50 w-full border-b bg-white shadow-sm" dir={t("dir")}>
                <div className="container mx-auto px-4 md:px-8 lg:px-12 h-20 flex items-center justify-between">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-6">
                        <img src={coLogo} alt={t("coName")} className="h-10" />
                        <div>
                            <div className="font-bold text-xl">{t("coName")}</div>
                        </div>
                    </Link>

                    {/* Desktop Menu */}
                    <nav className="hidden md:flex gap-2 flex-wrap justify-center items-center">
                        {/* الروابط الأساسية */}
                        {navLinks.map(link => (
                            <Link
                                key={link.href}
                                to={link.href}
                                className={`px-4 py-2 rounded-full text-sm font-medium ${location.pathname === link.href
                                    ? "bg-blue-100 text-blue-600 font-bold"
                                    : "text-gray-600 hover:text-blue-600"
                                    }`}
                            >
                                {link.label}
                            </Link>
                        ))}

                        {/* المزيد دائماً */}
                        <div className="relative group">
                            <button className="px-4 py-2 rounded-full text-sm font-medium text-gray-600 hover:text-blue-600"
                                onClick={() => { setMoreDropdownOpen(!moreDropdownOpen); setDropdownOpen(false) }}>
                                {t("more") || "المزيد"}
                                <span>{moreDropdownOpen ? "▲" : "▼"}</span>
                            </button>
                            {
                                moreDropdownOpen &&
                                <div className="flex flex-col mt-2 border rounded p-1 bg-white shadow-lg 
                                absolute bg-white shadow-lg rounded mt-2 min-w-[180px] z-50">
                                    {moreLinks.map(link => (
                                        link.separator ?
                                            <hr className="my-2 mb-0 mt-0 border-t border-gray-200" />
                                            :
                                            <Link
                                                key={link.href}
                                                to={link.href}
                                                className={`px-4 py-2 rounded-full text-sm font-medium ${location.pathname === link.href
                                                    ? "bg-blue-100 text-blue-600 font-bold"
                                                    : "text-gray-600 hover:text-blue-600"
                                                    }`}
                                                onClick={() => setMoreDropdownOpen(!moreDropdownOpen)}
                                            >
                                                {link.label}
                                            </Link>
                                    ))}
                                </div>
                            }
                        </div>
                    </nav>

                    {/* Actions */}
                    <div className="hidden md:flex items-center gap-3">
                        <LanguageSelector
                            currentLang={i18n.language}
                            changeLanguage={lng => {
                                i18n.changeLanguage(lng);
                                localStorage.setItem("traffic_lang", lng);
                            }}
                        />

                        {!user ? (
                            <button
                                className="flex items-center gap-1 px-4 py-2 rounded-md text-gray-700 hover:bg-gray-100"
                                onClick={() => setShowLogin(true)}
                            >
                                <UserCircle className="w-4 h-4 mr-1" />
                                {t("login")}
                            </button>
                        ) : (
                            <div className="relative" ref={dropdownRef}>
                                <button
                                    onClick={() => { setDropdownOpen(!dropdownOpen); setMoreDropdownOpen(false); }}
                                    className="flex items-center space-x-2 text-gray-700 hover:text-blue-600 font-medium"
                                >
                                    {user.Imagebase64 ? (
                                        <img
                                            src={`data:image/png;base64,${user.Imagebase64}`}
                                            alt="avatar"
                                            className="w-8 h-8 rounded-full"
                                        />
                                    ) : (
                                        <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">
                                            {user.Nickname || "U"}
                                        </div>
                                    )}
                                    <span className="truncate max-w-[120px]">{user.Username || t("user")}</span>
                                </button>

                                {dropdownOpen && (
                                    <div className="absolute right-0 mt-2 w-52 bg-white border border-gray-300 rounded shadow-lg z-50">
                                        <div className="px-4 py-3 border-b border-gray-200">
                                            <p className="text-gray-900 truncate">{user?.Username}</p>
                                            {user?.Email && <p className="text-gray-600 text-sm truncate">{user.Email}</p>}
                                            {user?.RoleName && <p className="text-gray-500 text-xs mt-1">{user.RoleName}</p>}
                                        </div>
                                        {user?.Role === 1 && (
                                            <Link to="/Trips" onClick={() => setDropdownOpen(false)} className="block px-4 py-2 text-gray-700 hover:bg-gray-100">
                                                {t("trips")}
                                            </Link>
                                        )}
                                        {user?.Role === 1 && (
                                            <Link to="/DriversMap" onClick={() => setDropdownOpen(false)} className="block px-4 py-2 text-gray-700 hover:bg-gray-100">
                                                {t("driverMap")}
                                            </Link>
                                        )}
                                        {user?.Role === 1 && (
                                            <Link to="/Users" onClick={() => setDropdownOpen(false)} className="block px-4 py-2 text-gray-700 hover:bg-gray-100">
                                                {t("users")}
                                            </Link>
                                        )}
                                        {canAccessTraffic && (
                                            <Link to="/TrafficJams" onClick={() => setDropdownOpen(false)} className="block px-4 py-2 text-gray-700 hover:bg-gray-100">
                                                {t("trafficJams")}
                                            </Link>
                                        )}
                                        {user && (
                                            <>
                                                {
                                                    user?.Role === 1 && <hr className="my-2 border-t border-gray-200" />
                                                }
                                                <button
                                                    className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 w-full text-left"
                                                    onClick={() => {
                                                        setIsPwdModalOpen(true);
                                                        setDropdownOpen(false);
                                                    }}
                                                >
                                                    <FaKey style={{ color: "#F2A741" }} />
                                                    {t("changePwd")}
                                                </button>
                                            </>
                                        )}
                                        <hr className="my-2 border-t border-gray-200" />
                                        <button
                                            onClick={handleLogout}
                                            className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 w-full text-left text-red-600"
                                        >
                                            <FaLock style={{ color: "#F2A741" }} />
                                            {t("logout")}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Mobile Button */}
                    <button className="md:hidden" onClick={() => setIsOpen(!isOpen)}>
                        <Menu className="w-6 h-6" />
                    </button>
                </div>

                {/* Mobile Menu */}
                {isOpen && (
                    <div
                        className={`md:hidden fixed top-20 ${isRTL ? "right-0" : "left-0"} w-72 max-h-[calc(100vh-5rem)] bg-white shadow-lg p-6 flex flex-col overflow-y-auto`}
                    >
                        {/* الروابط الأساسية */}
                        {navLinks.map(link => (
                            <Link
                                key={link.href}
                                to={link.href}
                                onClick={() => setIsOpen(false)}
                                className={`px-4 py-3 rounded-lg text-lg ${location.pathname === link.href ? "bg-blue-600 text-white" : "hover:bg-gray-100"
                                    }`}
                            >
                                {link.label}
                            </Link>
                        ))}

                        {/* المزيد دائماً */}
                        <div className="relative mt-2">
                            <button
                                className="w-full px-4 py-2 border rounded-md flex items-center justify-between hover:bg-gray-100"
                                onClick={() => { setMoreDropdownOpen(!moreDropdownOpen); dropdownOpen && setDropdownOpen(false); }}
                            >
                                {t("more") || "المزيد"}
                                <span>{moreDropdownOpen ? "▲" : "▼"}</span>
                            </button>

                            {moreDropdownOpen && (
                                <div className="flex flex-col gap-1 mt-2 pl-2 border-l border-gray-300">
                                    {moreLinks.map(link =>
                                        link.separator ? (
                                            <hr key="sep" className="my-2 border-gray-200" />
                                        ) : (
                                            <Link
                                                key={link.href}
                                                to={link.href}
                                                className={`px-4 py-2 rounded text-sm ${location.pathname === link.href
                                                    ? "bg-blue-600 text-white"
                                                    : "hover:bg-gray-100"
                                                    }`}
                                                onClick={() => {
                                                    setIsOpen(false);
                                                    setMoreDropdownOpen(false);
                                                }}
                                            >
                                                {link.label}
                                            </Link>
                                        )
                                    )}
                                </div>
                            )}
                        </div>

                        <hr className="my-2 border-gray-200" />

                        <LanguageSelector
                            currentLang={i18n.language}
                            changeLanguage={lng => {
                                i18n.changeLanguage(lng);
                                localStorage.setItem("traffic_lang", lng);
                                setIsOpen(false);
                            }}
                        />

                        {!user ? (
                            <button
                                className="w-full px-4 py-2 border rounded-md hover:bg-gray-100 mb-2 flex items-center justify-center gap-2"
                                onClick={() => {
                                    setShowLogin(true);
                                    setIsOpen(false);
                                }}
                            >
                                <UserCircle className="w-5 h-5" />
                                <span>{t("login")}</span>
                            </button>
                        ) : (
                            <div className="flex flex-col gap-2 mt-2">
                                <button
                                    onClick={() => { setDropdownOpen(!dropdownOpen); moreDropdownOpen && setMoreDropdownOpen(false) }}
                                    className="w-full px-4 py-2 border rounded-md flex items-center justify-between hover:bg-gray-100"
                                >
                                    <div className="flex items-center gap-2">
                                        {user.Imagebase64 ? (
                                            <img
                                                src={`data:image/png;base64,${user.Imagebase64}`}
                                                alt="avatar"
                                                className="w-8 h-8 rounded-full"
                                            />
                                        ) : (
                                            <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">
                                                {user.Nickname || "U"}
                                            </div>
                                        )}
                                        <span className="truncate max-w-[120px]">{user.Username || t("user")}</span>
                                    </div>
                                    <span className="ml-2">{dropdownOpen ? "▲" : "▼"}</span>
                                </button>

                                {
                                    dropdownOpen && (
                                        <div className="flex flex-col gap-1 mt-2 pl-2 border-l border-gray-300">
                                            {user?.Role === 1 && (
                                                <>
                                                    <Link to="/Trips" onClick={() => setIsOpen(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded">
                                                        {t("trips")}
                                                    </Link>
                                                    <Link to="/DriversMap" onClick={() => setIsOpen(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded">
                                                        {t("driverMap")}
                                                    </Link>
                                                    <Link to="/Users" onClick={() => setIsOpen(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded">
                                                        {t("users")}
                                                    </Link>
                                                </>
                                            )}
                                            {canAccessTraffic && (
                                                <Link to="/TrafficJams" onClick={() => setIsOpen(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded">
                                                    {t("trafficJams")}
                                                </Link>
                                            )}


                                            <button
                                                onClick={() => {
                                                    setIsPwdModalOpen(true);
                                                    setIsOpen(false);
                                                }}
                                                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded flex items-center gap-2"
                                            >
                                                <FaKey style={{ color: "#F2A741" }} />
                                                {t("changePwd")}
                                            </button>
                                            <button
                                                onClick={() => {
                                                    handleLogout();
                                                    setIsOpen(false);
                                                }}
                                                className="px-4 py-2 text-red-600 hover:bg-gray-100 rounded flex items-center gap-2"
                                            >
                                                <FaLock style={{ color: "#F2A741" }} />
                                                {t("logout")}
                                            </button>
                                        </div>
                                    )}
                            </div>
                        )}
                    </div>
                )}
            </header>

            {/* ================= LOGIN DIALOG ================= */}
            {showLogin &&
                createPortal(
                    <div className="fixed inset-0 z-[999999] flex items-center justify-center" dir={t("dir")}>
                        <div className="absolute inset-0 bg-black/60" onClick={() => setShowLogin(false)} />
                        <div
                            className="relative z-[1000000] w-full max-w-md mx-4 rounded-xl bg-white p-6 shadow-2xl"
                            onClick={e => e.stopPropagation()}
                        >
                            <button
                                onClick={() => setShowLogin(false)}
                                className="absolute top-3 right-4 text-xl text-gray-400 hover:text-red-600"
                            >
                                ✕
                            </button>
                            <h2 className="text-2xl font-bold mb-6 text-center">{t("login")}</h2>
                            {error && <p className="text-red-600 text-sm text-center mb-4">{error}</p>}

                            <input
                                type="text"
                                placeholder={t("username")}
                                value={username}
                                onChange={e => setUsername(e.target.value)}
                                autoFocus
                                className="w-full mb-3 rounded-lg border px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none uppercase"
                            />
                            <input
                                type="password"
                                placeholder={t("password")}
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                onKeyDown={e => e.key === "Enter" && handleLogin()}
                                className="w-full mb-5 rounded-lg border px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                            />
                            <button
                                onClick={handleLogin}
                                disabled={loading}
                                className="w-full rounded-lg bg-blue-600 py-2 text-white hover:bg-blue-700 transition"
                            >
                                {loading ? t("logging") : t("login")}
                            </button>
                        </div>
                    </div>,
                    document.body
                )}

            {isPwdModalOpen && <ChangePasswordModal isOpen={isPwdModalOpen} onClose={() => setIsPwdModalOpen(false)} />}
        </>
    );
}